import { getQuestionaireResponseByGigId } from "@gogig-in/gogig-clients";
import {
  Document,
  Page,
  Text,
  Image,
  View,
  StyleSheet,
  PDFViewer,
  Font,
} from "@react-pdf/renderer";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Typography,
  Paper,
  Container,
  Alert,
  AlertTitle,
} from "@mui/material";

// Register custom fonts
Font.register({
  family: "Roboto",
  fonts: [
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf",
    },
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf",
      fontWeight: 500,
    },
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf",
      fontWeight: 700,
    },
  ],
});

// PDF Styles optimized for single page layout
const styles = StyleSheet.create({
  viewer: {
    width: "100%",
    height: "100vh",
    border: "none",
  },
  page: {
    padding: 20, // Reduced padding to fit more content
    fontFamily: "Roboto",
    backgroundColor: "#FFFFFF",
  },
  header: {
    flexDirection: "row",
    marginBottom: 10, // Reduced margin
    paddingBottom: 5,
    borderBottom: "2px solid #1976d2",
  },
  headerLeft: {
    flex: 1,
  },
  headerRight: {
    flex: 1,
    alignItems: "flex-end",
  },
  reportTitle: {
    fontSize: 20, // Slightly smaller title
    fontWeight: "bold",
    color: "#1976d2",
    marginBottom: 5,
  },
  reportSubtitle: {
    fontSize: 12,
    color: "#555",
  },
  reportDate: {
    fontSize: 10,
    color: "#777",
  },
  section: {
    margin: 5, // Reduced margins
    padding: 10,
    backgroundColor: "#f5f5f5",
    borderRadius: 4,
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 8,
    color: "#333",
    borderBottom: "1px solid #ddd",
    paddingBottom: 3,
  },
  infoRow: {
    flexDirection: "row",
    marginBottom: 5,
  },
  infoLabel: {
    width: "30%",
    fontSize: 10, // Smaller font
    fontWeight: "bold",
    color: "#555",
  },
  infoValue: {
    width: "70%",
    fontSize: 10, // Smaller font
    color: "#333",
  },
  // Larger image container
  imageContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginTop: 5,
  },
  // Larger image wrapper
  imageWrapper: {
    width: "48%", // Two images per row
    marginBottom: 10,
  },
  // Larger images
  image: {
    width: "100%",
    height: 180, // Increased height
    objectFit: "contain", // Changed to contain to show full image
    borderRadius: 3,
  },
  imageCaption: {
    fontSize: 8,
    textAlign: "center",
    marginTop: 2,
    color: "#666",
  },
  footer: {
    position: "absolute",
    bottom: 15, // Moved up
    left: 20,
    right: 20,
    textAlign: "center",
    fontSize: 8, // Smaller footer
    color: "#777",
    borderTop: "1px solid #ddd",
    paddingTop: 5,
  },
  // Compact row for driver and location
  compactRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  compactColumn: {
    width: "48%",
  },
});

// Helper function to format date
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Interface definitions
interface LocationCoordinates {
  latitude: number;
  longitude: number;
}

interface DriverDetails {
  name?: string;
  rcNumber?: string;
  phoneNumber?: string;
}

interface ReportData {
  "Driver Details: "?: DriverDetails;
  "Installation Location"?: LocationCoordinates;
  Images?: string[];
  "Upload 4 images of the auto with the top installed from left, right, front and back side"?: string[];
  date?: string;
  [key: string]: any;
}

// Reverse geocoding function
// async function reverseGeocode(lat: number, lng: number): Promise<string> {
//   try {
//     const response = await fetch(
//       `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
//     );
//     const data = await response.json();

//     if (data.error) {
//       return "Location not found";
//     }

//     const address = data.address || {};
//     const parts = [
//       address.road,
//       address.neighbourhood,
//       address.suburb,
//       address.city,
//       address.state,
//       address.country
//     ].filter(Boolean);

//     return parts.join(", ") || "Location not available";
//   } catch (error) {
//     console.error("Geocoding error:", error);
//     return "Location service unavailable";
//   }
// }

function CampaignReportPDF() {
  const [reportData, setReportData] = useState<ReportData[]>([]);
  // const [locationNames, setLocationNames] = useState<{[key: string]: string}>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { gigId: paramGigId } = useParams();
  const gigId = paramGigId || "c5524aab-d10f-4437-8a7d-6a50b1a8f636";

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!gigId) {
          setError("No gig ID provided");
          setLoading(false);
          return;
        }

        const response: any = await getQuestionaireResponseByGigId(
          gigId,
          1,
          1000
        );
        const resultData = response.data;
        setReportData(resultData);

        // const names: {[key: string]: string} = {};
        // for (const item of resultData) {
        //   if (item["Installation Location"]) {
        //     const { latitude, longitude } = item["Installation Location"];
        //     const key = `${latitude},${longitude}`;

        //     if (!names[key]) {
        //       names[key] = await reverseGeocode(latitude, longitude);
        //     }
        //   }
        // }
        // setLocationNames(names);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load report data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [gigId]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f5f5f5"
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <CircularProgress size={60} color="primary" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading Report Data...
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!reportData.length) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            No Report Data Available
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please check the gig ID parameter or try again later.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{ width: "100%", height: "100vh" }}>
      <PDFViewer style={styles.viewer}>
        <Document>
          {reportData.map((data, index) => {
            // const locationKey = data["Installation Location"]
            //   ? `${data["Installation Location"].latitude},${data["Installation Location"].longitude}`
            //   : null;
            // const locationName = locationKey ? locationNames[locationKey] : null;

            const images =
              data.Images ||
              data[
                "Upload 4 images of the auto with the top installed from left, right, front and back side"
              ] ||
              [];
            const driverDetails = data["Driver Details: "];
            const installationDate = data.date ? formatDate(data.date) : "N/A";
            const imageCaptions = [
              "Front View",
              "Right Side",
              "Left Side",
              "Back View",
            ];

            return (
              <Page key={index} size="A4" style={styles.page}>
                {/* Header */}
                <View style={styles.header}>
                  <View style={styles.headerLeft}>
                    <Text style={styles.reportTitle}>Installation Report</Text>
                    <Text style={styles.reportSubtitle}>
                      Report #{index + 1} | ID: {gigId}
                    </Text>
                    <Text style={styles.reportDate}>{installationDate}</Text>
                  </View>
                  <View style={styles.headerRight}>
                    <Text style={{ fontSize: 8, color: "#777" }}>
                      Generated on {new Date().toLocaleDateString()}
                    </Text>
                  </View>
                </View>

                {/* Driver and Location in a compact row */}
                {driverDetails && (
                  <View style={styles.compactRow}>
                    {/* Driver Information */}
                    <View style={styles.compactColumn}>
                      <View style={styles.section}>
                        <Text style={styles.sectionTitle}>
                          Driver Information
                        </Text>

                        <View style={styles.infoRow}>
                          <Text style={styles.infoLabel}>Name:</Text>
                          <Text style={styles.infoValue}>
                            {driverDetails.name || "N/A"}
                          </Text>
                        </View>

                        <View style={styles.infoRow}>
                          <Text style={styles.infoLabel}>RC Number:</Text>
                          <Text style={styles.infoValue}>
                            {driverDetails.rcNumber || "N/A"}
                          </Text>
                        </View>

                        <View style={styles.infoRow}>
                          <Text style={styles.infoLabel}>Phone Number:</Text>
                          <Text style={styles.infoValue}>
                            {driverDetails.phoneNumber || "N/A"}
                          </Text>
                        </View>
                      </View>
                    </View>

                    {/* Location Information */}
                    {/* <View style={styles.compactColumn}>
                    {data["Installation Location"] ? (
                      <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Installation Location</Text>
                        
                        <View style={styles.infoRow}>
                          <Text style={styles.infoLabel}>Address:</Text>
                          <Text style={styles.infoValue}>{locationName || "Fetching location..."}</Text>
                        </View>
                        
                        <View style={styles.infoRow}>
                          <Text style={styles.infoLabel}>Coordinates:</Text>
                          <Text style={styles.infoValue}>
                            {data["Installation Location"].latitude?.toFixed(6)}, {data["Installation Location"].longitude?.toFixed(6)}
                          </Text>
                        </View>
                      </View>
                    ) : (
                      <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Installation Location</Text>
                        <Text style={styles.infoValue}>No location data available</Text>
                      </View>
                    )}
                  </View> */}
                  </View>
                )}

                {/* Images section */}
                <View style={styles.section}>
                  <Text style={styles.sectionTitle}>Installation Images</Text>

                  <View style={styles.imageContainer}>
                    {images.map((imgUrl: string, imgIndex: number) => (
                      <View key={imgIndex} style={styles.imageWrapper}>
                        <Image style={styles.image} src={imgUrl} />
                        <Text style={styles.imageCaption}>
                          {imageCaptions[imgIndex] || `Image ${imgIndex + 1}`}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>

                {/* Footer */}
                <View style={styles.footer} fixed>
                  <Text>
                    © {new Date().getFullYear()} GoGig Installation Report |
                    Page {index + 1} of {reportData.length}
                  </Text>
                </View>
              </Page>
            );
          })}
        </Document>
      </PDFViewer>
    </Box>
  );
}

export default CampaignReportPDF;
